"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowUpRight, Heart, Mail, Zap } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";

const FOOTER_NAV = [
  {
    title: "Product",
    links: [
      { label: "Pricing", href: "/pricing" },
      { label: "FAQ", href: "/faq" },
      { label: "Contact", href: "/contact" },
      { label: "Promo Code", href: "/promo" },
      { label: "Invite a friend", href: "/referral" },
    ],
  },
  {
    title: "Learn",
    links: [{ label: "Events", href: "/events" }],
  },
  {
    title: "Company",
    links: [{ label: "About", href: "/about" }],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
      { label: "Accessibility", href: "/accessibility" },
    ],
  },
];

const SOCIALS = [
  { label: "X / Twitter", href: "https://x.com/1stuniui_com_ng", short: "𝕏" },
  { label: "TikTok", href: "https://tiktok.com/@1stuniui_com_ng", short: "TT" },
  { label: "GitHub", href: "https://github.com/uniui", short: "GH" },
  {
    label: "LinkedIn",
    href: "https://linkedin.com/company/uniui",
    short: "in",
  },
];

const CONTAINER_VARIANTS = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.07, delayChildren: 0.1 },
  },
};

const COL_VARIANTS = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
  },
};

export default function SiteFooter() {
  const [logoError, setLogoError] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    // Optimistic UI — show "Subscribed" immediately, even offline.
    setSubscribed(true);
    setEmail("");
    // Persist the signup; failures are non-fatal (offline / rate-limited).
    apiFetch("/api/v1/subscribe", {
      method: "POST",
      body: JSON.stringify({
        email: value,
        source: "footer",
        page: window.location.pathname,
      }),
    }).catch(() => {});
  };

  return (
    <footer className="relative overflow-hidden border-t border-border bg-background">
      {/* Top accent line */}
      <div
        className="absolute inset-x-0 top-0 h-[1px] bg-gradient-to-r from-transparent via-y/40 to-transparent"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-6xl px-6 py-14">
        <motion.div
          variants={CONTAINER_VARIANTS}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-2 gap-10 md:grid-cols-6"
        >
          {/* Brand + newsletter */}
          <motion.div variants={COL_VARIANTS} className="col-span-2 space-y-5">
            <Link
              href="/"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              {!logoError ? (
                <Image
                  src="/uniui_original_remastered_primary.png"
                  alt="UniUI"
                  width={108}
                  height={36}
                  className="h-9 w-auto"
                  onError={() => setLogoError(true)}
                />
              ) : null}
              <span className="font-display text-lg font-black tracking-tight text-foreground">
                UniUI
              </span>
            </Link>

            <p className="max-w-xs text-xs font-medium leading-relaxed text-muted-foreground">
              Academic intelligence built for Nigerian university students. Ask
              better questions, study smarter, go further.
            </p>

            <div className="flex items-center gap-2">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="group flex h-9 w-9 items-center justify-center rounded-lg border border-border text-xs font-black uppercase tracking-widest text-muted-foreground transition-all duration-200 hover:border-y hover:text-y hover:shadow-[0_0_10px_rgba(252,209,83,0.2)]"
                >
                  {s.short}
                </a>
              ))}
            </div>

            {/* Newsletter */}
            <form onSubmit={handleSubscribe} className="space-y-2">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-foreground/80">
                Product updates
              </p>
              <div className="flex gap-2">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@uni.edu.ng"
                  className="h-10 flex-1 rounded-xl border border-border bg-background px-3 text-xs font-medium text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-y focus:ring-1 focus:ring-y/40"
                />
                <button
                  type="submit"
                  className="h-10 rounded-xl bg-y px-3 text-xs font-black uppercase tracking-widest text-black transition-colors hover:bg-y-light"
                >
                  <Zap size={14} />
                </button>
              </div>
              {subscribed && (
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-y">
                  Subscribed
                </p>
              )}
            </form>
          </motion.div>

          {/* Nav columns */}
          {FOOTER_NAV.map((col) => (
            <motion.div
              key={col.title}
              variants={COL_VARIANTS}
              className="space-y-3"
            >
              <h3 className="text-xs font-black uppercase tracking-[0.25em] text-y">
                {col.title}
              </h3>
              <ul className="space-y-2">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="group inline-flex items-center gap-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {link.label}
                      <ArrowUpRight
                        size={10}
                        className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-200"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom bar */}
        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-border pt-6 sm:flex-row">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-muted-foreground">
            © {new Date().getFullYear()} UniUI — All rights reserved
          </p>
          <a
            href="mailto:support@uniui.com.ng"
            className="group inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:text-y"
          >
            <Mail size={12} aria-hidden="true" />
            support@uniui.com.ng
            <ArrowUpRight
              size={10}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
              aria-hidden="true"
            />
          </a>
        </div>
      </div>
    </footer>
  );
}
